import { useEffect } from 'react'
import { StringUtils, StorageUtils, BrowserUtils, QuestionUtils } from '../utils/siteUtils'
import { useGlobalState } from './GlobalStateProvider'
import useDataFetcher from './useDataFetcher'

const useEdgeDataFetcher = (service, postdata, method, hookName, useCache = true, headers = {}, requireAuthToken = false, returnAll = false, contentType = 'text/json', cancelRequest = false) => {
  const { globalVariables, globalState, updateGlobalVariables } = useGlobalState()
  const { environment } = globalState
  const { traceId, authToken, refreshToken } = globalVariables.current || {}
  const serviceUrl = service && environment.ico_EdgeApiUrl + service + '&api_key=' + environment.ico_EdgeApiKey

  if (environment.ico_UseBasicAuth) headers.Authorization = `Basic ${environment.ico_Token}`
  headers.QuestionConversion = 'fury'
  headers.TraceId = traceId
  if (authToken) headers.authtoken = authToken

  const canConnect = authToken ? !refreshToken : !requireAuthToken
  const [edgeData, ...otherDataFetcherProps] = useDataFetcher(canConnect && serviceUrl, useCache, postdata, JSON.stringify(headers), method, hookName, contentType, undefined, cancelRequest)
  const refreshTokenNeeded = edgeData && edgeData.meta && edgeData.meta.codes.find(code => code === 403001 || code === 403002 || code === 403003)
  if (refreshTokenNeeded && !refreshToken) {
    updateGlobalVariables({ refreshToken: true })
    BrowserUtils.postExpiredToken()
  }
  if (returnAll) return [edgeData, ...otherDataFetcherProps]
  return [edgeData && edgeData.data, (edgeData && edgeData.meta && edgeData.meta.codes.filter(code => code !== 200000)) || [], ...otherDataFetcherProps]
}
export default useEdgeDataFetcher

const getItemFromDisplayName = (param, items, inPath = true, propId) =>
  param &&
  items &&
  (inPath //
    ? items.find(item => StringUtils.toUrlDisplayNamePath(item.displayName) === '' + param)
    : items.find(item => item[propId] === +param))

const useEdgeDataFetcherItems = (service, param, inPath, propId, getFirst, getIfOneOtherwiseGetDefault, pageContextData) => {
  const [itemsLoaded, errors, isFetching, resetData] = useEdgeDataFetcher(!pageContextData && service)
  const items = itemsLoaded || pageContextData
  const itemFound = getItemFromDisplayName(param, items, inPath, propId)
  const item = itemFound || (getFirst && items && items.length ? items[0] : null) || (getIfOneOtherwiseGetDefault && (items && items.length === 1 ? items[0] : items && items.find && items.find(item => item.isDefault && item.isSelected))) || null
  return { items, item, isFetching, resetData, errors }
}

export const useEdgeDataFetcherYears = (param, inPath, pageContextYears) => useEdgeDataFetcherItems(`/vehicles/years?a=b`, param, inPath, 'yearId', false, false, pageContextYears)
export const useEdgeDataFetcherMakes = (year, param, inPath) => useEdgeDataFetcherItems(year && `/vehicles/makes?yearId=${year.yearId}`, param, inPath, 'makeId')
export const useEdgeDataFetcherModels = (year, make, param, inPath) => useEdgeDataFetcherItems(year && make && `/vehicles/models?yearId=${year.yearId}&makeId=${make.makeId}`, param, inPath, 'modelId')

const CURRENT_YEAR = new Date().getFullYear()
export const useEdgeDataFetcherYMM = (pageContext, yearSelect, makeSelect, modelSelect, inPath) => {
  const { items: years, item: year } = useEdgeDataFetcherYears(yearSelect, inPath, pageContext.years && pageContext.years[0] && pageContext.years[0].yearId === CURRENT_YEAR ? pageContext.years : undefined)
  const { items: makes, item: make } = useEdgeDataFetcherMakes(year, makeSelect, inPath)
  const { items: models, item: model } = useEdgeDataFetcherModels(year, make, modelSelect, inPath)
  return { years, makes, models, year, make, model }
}

export const useEdgeDataFetcherTrims = (landingContext, pageContext, location) => {
  const { vinResults, year, make, model, urlState, setLoadingInfo, offer } = landingContext
  const vinData = vinResults || {}
  let { trim: trimQueryName } = location ? location.initQueryParams : {}
  trimQueryName = trimQueryName && trimQueryName.replace(/\+/g, ' ').toLowerCase()

  const selectedStyle = urlState.s || (offer && offer.prospect.vehicle.trimId)
  const { items: trims, item: trim, isFetching: isFetchingTrims } = useEdgeDataFetcherItems(year && make && model && `/vehicles/trims?yearId=${year.yearId}&makeId=${make.makeId}&modelId=${model.modelId}`, selectedStyle, false, 'trimId', false, true)
  setLoadingInfo('Getting Vehicles', isFetchingTrims)

  //preserve body style when vin decode
  if (vinData && vinData.trims && trims) {
    vinData.trims = vinData.trims.map(vinTrim => {
      const bodyStyle = (trims.find(t => +t.trimId === +vinTrim.trimId) || {}).bodyStyle || ''
      return { ...vinTrim, bodyStyle }
    })
    if (vinData.trim) vinData.trim.bodyStyle = (trims.find(t => +t.trimId === +vinData.trim.trimId) || {}).bodyStyle || ''
  }
  landingContext.trims = vinData.trims || trims || pageContext.trims
  const trimQuery = trimQueryName && landingContext.trims && landingContext.trims.find(i => i.displayName.toLowerCase() === trimQueryName)
  if (landingContext.trims && landingContext.trims.length > 1) landingContext.trim = (selectedStyle && trim) || trimQuery || trim || undefined
  else landingContext.trim = vinData.trim || (selectedStyle && trim) || trimQuery || trim || undefined
  if (!landingContext.trim && process.env.BUILD_STAGE === 'build-html') landingContext.trim = pageContext.trim
  landingContext.trim = landingContext.trim && { ...landingContext.trim, bodyStyle: (landingContext.trim && landingContext.trim.bodyStyle) || '' }
  landingContext.bodyStyle = landingContext.trim && landingContext.trim.bodyStyle.charAt(0) + landingContext.trim.bodyStyle.slice(1).toLowerCase()

  if (location && location.state && location.state.trim) landingContext.trim = location.state.trim
}

export const useEdgeDataFetcherForTrim = (trim, trimprop, param, getFirst = false, getIfOneOtherwiseGetDefault = true) =>
  useEdgeDataFetcherItems(trim && `/vehicles/${trimprop}?trimId=${trim}`, param, false, `${trimprop.substr(0, trimprop.length - 1)}Id`, getFirst, getIfOneOtherwiseGetDefault)

export const useEdgeDataFetcherEligibility = (landingContext, pageContext) => {
  const { items: eligibility, isFetching: isFetchingEligibility } = useEdgeDataFetcherForTrim(landingContext.trim && landingContext.trim.trimId, 'eligibility')
  landingContext.eligibility = eligibility || pageContext.eligibility
  landingContext.setLoadingInfo('Check Eligibility', isFetchingEligibility)
}

export const useEdgeDataFetcherImages = (landingContext, pageContext) => {
  const trimId = (landingContext.trim && landingContext.trim.trimId) || (landingContext.trims && landingContext.trims[0] && landingContext.trims[0].trimId)
  const { items: images, isFetching } = useEdgeDataFetcherForTrim(trimId, 'images')
  const same = pageContext.trim && pageContext.trim.trimId === trimId
  if (isFetching) landingContext.images = same ? pageContext.images : pageContext.images || !pageContext.trim ? { url: null } : undefined
  else landingContext.images = images || (!trimId && { url: null })
}

export const useEdgeDataFetcherDetail = (landingContext, pageContext, detail) => {
  const details = detail + 's'
  const eligible = landingContext.eligibility && landingContext.eligibility.isEligible
  const isAssigned = landingContext.experienceState && landingContext.experienceState.isAssigned
  const allowIneligibleVehicleForCobranding = allowIneligibleVehicleCobranding(landingContext)
  const allowIneligibleVehicles = isAssigned || allowIneligibleVehicleForCobranding
  const isIneligibleVehiclesEnabled = landingContext.experienceState && landingContext.experienceState.isIneligibleVehiclesEnabled
  const { urlState, offer, vinResults, trim } = landingContext
  const idParam = detail + 'Id'
  let selectedDetail = urlState[detail.charAt(0)]
  if (!selectedDetail && offer && offer.prospect.vehicle[idParam]) selectedDetail = offer.prospect.vehicle[idParam]

  const vinData = vinResults || {}
  const { items: itemsFetched, item: itemFetched, isFetching } = useEdgeDataFetcherForTrim((eligible || (isIneligibleVehiclesEnabled ? allowIneligibleVehicles : false)) && trim && trim.trimId, details, selectedDetail, false, false)
  const items = itemsFetched
  const item = itemFetched

  const detailItems = vinData[details] || items
  let detailItem
  if (typeof vinData[detail] !== 'undefined' || detail === 'color') detailItem = vinData[detail] || item
  else detailItem = (items && items.length === 1 && items[0]) || item

  const detailIneligible = eligible && landingContext.eligibility['excluded' + detail.charAt(0).toUpperCase() + detail.slice(1) + 's']
  landingContext[details] = (detailItems && detailItems.map(item => (detailIneligible && detailIneligible.findIndex(inel => inel === item[idParam]) !== -1 ? { ...item, isIneligible: true } : item))) || pageContext[details]
  const detailObject = detailItem && detailIneligible && detailIneligible.findIndex(inel => inel === detailItem[idParam]) !== -1 ? { ...detailItem, isIneligible: true } : detailItem
  if (detailObject) landingContext[detail] = detailObject

  if (!landingContext[detail] && process.env.BUILD_STAGE === 'build-html' && !!landingContext[details]) landingContext[detail] = landingContext[details][0]

  if (!landingContext[detail] && landingContext[details] && landingContext[details].length === 1) landingContext[detail] = landingContext[details][0]

  landingContext.setLoadingInfo(`Getting Vehicle Information`, isFetching)
}

export const useEdgeDataFetcherOptions = (landingContext, pageContext) => {
  const { updateGlobalState } = useGlobalState()
  const vinCheck = landingContext.vinModalInfo.vinCheck
  const isAssigned = landingContext.experienceState && landingContext.experienceState.isAssigned
  const allowIneligibleVehicleForCobranding = allowIneligibleVehicleCobranding(landingContext)
  const allowIneligibleVehicles = isAssigned || allowIneligibleVehicleForCobranding

  const isIneligibleVehiclesEnabled = landingContext.experienceState && landingContext.experienceState.isIneligibleVehiclesEnabled

  const eligible = landingContext.eligibility && landingContext.eligibility.isEligible
  const trimId = (eligible || (isIneligibleVehiclesEnabled ? allowIneligibleVehicles : false)) && landingContext.trim && landingContext.trim.trimId
  const trimChangedFromOffer = landingContext.offer && trimId !== landingContext.offer.prospect.vehicle.trimId

  const vinDecodeOptions = landingContext && landingContext.vinResults && landingContext.vinResults.options
  const urlStateOptions = landingContext.urlState.o
  const offerPassedOptions = landingContext.offer && !trimChangedFromOffer ? landingContext.offer.prospect.options : []
  const offerUrlStateO = offerPassedOptions.filter(option => option.isSelected !== option.isDefault).map(({ optionId, isSelected }) => optionId.toString() + (isSelected ? 'T' : 'F'))
  const userSelectedOptions = urlStateOptions ? offerUrlStateO.concat(urlStateOptions) : offerUrlStateO

  const preparedChanges = userSelectedOptions ? userSelectedOptions.map(option => ({ optionId: +option.substr(0, option.length - 1), isSelected: option.substr(option.length - 1) === 'T' })) : []
  //^ maps minified urlstate to an array of options
  const { items: optionsFetched, isFetching: isFetchingOptions } = useEdgeDataFetcherForTrim(trimId, 'options')
  const options = vinDecodeOptions || optionsFetched || pageContext.options
  //^ make initial get for default options
  landingContext.setLoadingInfo('Getting Vehicle Options', isFetchingOptions)
  const latest = preparedChanges.slice(preparedChanges.length - 1)[0]
  landingContext.mergeOptionArray(options, offerPassedOptions ? offerPassedOptions : [])
  landingContext.mergeOptionArray(landingContext.options, [])
  //^ grab the latest change and merge it in. show the default options
  const initialOptionsState =
    offerPassedOptions &&
    options &&
    offerPassedOptions
      .map(option => ({ ...options.find(opt => +opt.optionId === +option.optionId), isSelected: option.isSelected }))
      .filter(a => a.hasOptionRelationships)
      .map(a => `${a.optionId}|${a.isSelected}`)
      .join('|')

  const newOptionsState = urlStateOptions && urlStateOptions.reduce((acc, cur) => acc + `${cur.replace(/[^\d]/, '')}|${cur.replace(/\d+/, '') === 'T'}|`, '')
  const optionsState = (initialOptionsState ? initialOptionsState + '|' : '') + (newOptionsState ? newOptionsState : '')
  const [changes, , isFetchingChanges] = useEdgeDataFetcher(trimId && optionsState && `/vehicles/options/lastchanged/?trimid=${trimId}&optionsState=${optionsState}`)
  !vinCheck && !isFetchingChanges && changes && changes.length && latest && landingContext.applySystemChange(changes, latest)
  // //^ diff this step and last step to determine what changed to show the update Triangle thingy
  landingContext.mergeOptionArray(landingContext.options, changes || [])
  //^ merge final changes
  updateGlobalState({ isFetchingChanges: isFetchingChanges })
}

export const useEdgeDataFetcherVehicleSpecs = landingContext => {
  const eligible = landingContext.eligibility && landingContext.eligibility.isEligible
  const isAssigned = landingContext.experienceState && landingContext.experienceState.isAssigned
  const allowIneligibleVehicleForCobranding = allowIneligibleVehicleCobranding(landingContext)
  const allowIneligibleVehicles = isAssigned || allowIneligibleVehicleForCobranding
  const isIneligibleVehiclesEnabled = landingContext.experienceState && landingContext.experienceState.isIneligibleVehiclesEnabled
  const { items: vehicleSpecs } = useEdgeDataFetcherForTrim((eligible || (isIneligibleVehiclesEnabled ? allowIneligibleVehicles : false)) && landingContext.trim && landingContext.trim.trimId, 'vehicleSpecs')
  const numberOfDoorsSpec = vehicleSpecs ? vehicleSpecs.find(obj => obj.specId === 237) : undefined
  const maximumSeatingCapacitySpec = vehicleSpecs ? vehicleSpecs.find(obj => obj.specId === 229) : undefined
  landingContext.exteriorDoorCount = (numberOfDoorsSpec ? numberOfDoorsSpec.value : 0) === '2' ? '2' : '4'
  landingContext.interiorSeatRows = ['2', '3'].includes(maximumSeatingCapacitySpec ? maximumSeatingCapacitySpec.value : 0) ? '1' : '2'
}

const allowIneligibleVehicleCobranding = landingContext => {
  const isCobrand = landingContext.experienceState && landingContext.experienceState.isCobrand
  const allowIneligibleVehicleForCobranding = landingContext.experienceState && landingContext.experienceState.allowIneligibleVehicle && isCobrand
  return allowIneligibleVehicleForCobranding
}

const prospectDifferent = (prospectData, prospectStored) => {
  if (
    prospectStored &&
    (prospectData.vehicle.yearId !== prospectStored.vehicle.yearId ||
      prospectData.vehicle.makeId !== prospectStored.vehicle.makeId ||
      prospectData.vehicle.modelId !== prospectStored.vehicle.modelId ||
      prospectData.vehicle.trimId !== prospectStored.vehicle.trimId)
  )
    return true
  return false
}

const prospectNeedsPatch = (prospectData, prospectStored) => {
  const shouldPatch =
    prospectStored &&
    !(
      Object.keys(prospectData.vehicle).reduce((acc, key) => acc && prospectData.vehicle[key] === prospectStored.vehicle[key], true) &&
      Object.keys(prospectData)
        .filter(key => key !== 'vehicle')
        .reduce((acc, key) => acc && (!(prospectData[key] && typeof prospectData[key] !== 'object') || `${prospectData[key]}` === `${prospectStored[key]}`), true)
    )
  return shouldPatch
}

export const useEdgePatchProspectOffer = (prospectId, prospectData, offerId, isAdmin, offer) => {
  const isEditOffer = offer && !offer.isExpired
  const prospectPost = prospectData && JSON.stringify(prospectData)
  let path = prospectId && prospectPost && `/prospects/${prospectId}${isAdmin ? '/dealer' : ''}?a=b&isEditOffer=${isEditOffer === undefined ? false : isEditOffer}`
  const [prospectFetchedPatched, ...others] = useEdgeDataFetcher(path, prospectPost, 'PATCH', 'patch prospect', 'last')
  const prospectFetched = path && prospectFetchedPatched
  const [errors, isFetching] = others
  const error = !isFetching && errors && errors[0] && errors[0] !== 201000
  if (!error && prospectFetched) prospectFetched.prospectId = prospectId
  useEffect(() => {
    if (!error && prospectFetched)
      if (prospectFetched.prospectId) StorageUtils.setProspectWithOneDayExpiration(JSON.stringify(prospectFetched), offerId)
      else console.log('no prospectId on prospect patch', prospectFetched)
  }, [error, prospectFetched, offerId])
  const hasProspectId = prospectFetched && prospectFetched.prospectId
  return [hasProspectId && prospectFetched, ...others]
}

export const useEdgeDataGetProspect = (prospectData, offer, renew, urlState, prospectId, allowIneligbleSubmissionWorkflow) => {
  let prospectStored = null
  let prospectPost = false
  let shouldPatch = false
  const offerId = offer && offer.offerId
  if (prospectData || offerId) {
    const storedProspectStr = StorageUtils.getProspectWithExpiration(offerId)
    prospectStored = storedProspectStr && JSON.parse(storedProspectStr)
    if (!offerId && prospectDifferent(prospectData, prospectStored)) prospectStored = null
    shouldPatch = !offerId && (prospectNeedsPatch(prospectData, prospectStored) || !!prospectId)
    if (prospectData && (!prospectStored || shouldPatch)) prospectPost = JSON.stringify(prospectData)
  }
  const offerUsedCads = offer && offer.prospect.vehicle.vehicleBuildDataFlags && offer.prospect.vehicle.vehicleBuildDataFlags.cadsUsed
  const offerHasVin = offer && offer.prospect.vehicle.vin
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////CADS LOGIC///////
  const isNotConsumerResubmitOffer = urlState && urlState.p !== 13 && urlState.pa && urlState.pa.includes('reviewoffer')
  const newOfferBypassFlag = !offer // new offers: if true, then use cads
  const resubmitBypassFlag = offer && offer.isExpired && isNotConsumerResubmitOffer // if offer resubmit, but not in conumer. use cads
  const edgeCaseBypassFlags = !offerHasVin || !offerUsedCads // if offer is edited and is cads keep cads, if offer is resubmit and has vin use cads

  // this is the old bypass logic: (revert to original logic 6-18-2021)
  const cadsBypassFlag = newOfferBypassFlag ? false : resubmitBypassFlag ? false : edgeCaseBypassFlags ? true : false

  // this is the new to keep it off for the time being:
  //const cadsBypassFlag = !offerUsedCads ? true : false
  const vinVerifiedFlag = true
  const modifyRenewFlags = `&cadsBypass=${cadsBypassFlag}&vinVerified=${vinVerifiedFlag}`
  const isEditOffer = offer && !offer.isExpired
  let path = prospectPost && (shouldPatch ? `/prospects/${(prospectStored && prospectStored.prospectId) || prospectId}?a=b&isEditOffer=${isEditOffer === undefined ? false : isEditOffer}` : '/prospects?a=b')
  if (!path && offerId && !prospectStored) path = '/offers/' + offerId + '/' + (renew ? 'renew' : 'modify') + `?a=b${modifyRenewFlags}&isEditOffer=${renew ? 'false' : isEditOffer === undefined ? false : isEditOffer}`
  const [prospectFetched, ...others] = useEdgeDataFetcher(path, prospectPost || ' ', shouldPatch ? 'PATCH' : 'POST', 'prospect', 'last')
  const errors = others[0]
  const error = !path || others[1] || (errors && errors[0] && (allowIneligbleSubmissionWorkflow ? !(errors[0] === 200422 || errors[0] === 201000) : errors[0] !== 201000))

  if (!error && prospectFetched && offer) Object.keys(offer.prospect).forEach(key => (prospectFetched[key] = prospectFetched[key] || offer.prospect[key]))

  if (!prospectStored && !error && prospectFetched) prospectStored = prospectFetched
  else if (prospectStored && shouldPatch && !error && prospectFetched) Object.keys(prospectFetched).forEach(key => (prospectStored[key] = prospectFetched[key]))

  const hasProspectId = prospectStored && prospectStored.prospectId
  const prospectStoredStr = prospectStored ? JSON.stringify(prospectStored) : undefined

  useEffect(() => {
    if (prospectStoredStr) {
      if (!hasProspectId) console.log('prospect has no prospectId')
      else StorageUtils.setProspectWithOneDayExpiration(prospectStoredStr, offerId)
    }
  }, [hasProspectId, prospectStoredStr, offerId])

  return [hasProspectId && prospectStored, ...others]
}

export const useEdgeDataFindDuplicateOffer = requestBody => useEdgeDataFetcher(requestBody && '/offer/duplicate-offer-search-full?a=b', JSON.stringify(requestBody), 'POST')

export const useEdgeDataProspectDocuments = prospectId => useEdgeDataFetcher(prospectId && `/prospects/${prospectId}/documents?a=b`)

export const useEdgeDataProspectQx = prospectId => useEdgeDataFetcher(prospectId && `/prospects/${prospectId}/other?a=b`)

export const useEdgePatchProspectOptions = (prospectId, changedOptions, vinResults, offer) =>
  useEdgeDataFetcher(
    prospectId &&
      changedOptions &&
      `/prospects/${prospectId}/options?updateTED=false${offer && !offer.prospect.vehicle.vin && vinResults && vinResults.optionsVinVerified ? `&vinVerified=${vinResults.optionsVinVerified}` : ''}${offer && !offer.isExpired ? '&isEditOffer=true' : ''}`,
    JSON.stringify(changedOptions),
    'PATCH',
    'patch options',
    'last'
  )

export const useEdgeDataFetcherDealer = dealerId => useEdgeDataFetcher(dealerId && `/dealers/${dealerId}/?a=b`)

export const useEdgeRenewOffer = offerId => useEdgeDataFetcher(`/offers/${offerId}/renew?a=b`)

export const useEdgeBulkPatchOffer = (prospectId, questions) => {
  const body = prospectId && questions && JSON.stringify(questions)
  const [patchData, patchError, isFetching] = useEdgeDataFetcher(prospectId && body && `/prospects/${prospectId}/answers?a=b`, body, 'PUT', 'answers', 'last')
  return [!isFetching && patchData, !isFetching && patchError]
}

export const useEdgeSubmitOffer = prospectId => {
  const [offerSubmitData, offerSubmitError, isFetching] = useEdgeDataFetcher(prospectId && `/offers/?prospectId=${prospectId}`, ' ', 'POST', 'submit', false)
  return [!isFetching && offerSubmitData, !isFetching && offerSubmitError]
}

export const useEdgeGetOffer = offerId => useEdgeDataFetcher(offerId && `/offers/${offerId}?a=b`)

export const useEdgeGetVehicleDetails = prospectId => useEdgeDataFetcher(prospectId && `/vehicle/prospectId/${prospectId}?a=b`)

export const useEdgeGetOfferDetails = offerId => useEdgeDataFetcher(offerId && `/offers/details/${offerId}?a=b`)

export const useEdgeGetOfferDetailsWithDealerGuid = (offerId, encryptedDealerGuid) => useEdgeDataFetcher(offerId && `/offers/details/${offerId}?a=b&did=${encodeURIComponent(encryptedDealerGuid)}`)

export const useEdgeSendOffer = (offerId, dealerId) => useEdgeDataFetcher(offerId && dealerId && `/offers/${offerId}/dealer/${dealerId}?a=b`)

export const useEdgeGetCobrandingGroupInfo = cobrandingGroupId => useEdgeDataFetcher(cobrandingGroupId && `/cobranding/groups/${cobrandingGroupId}?a=b`)

export const useEdgeGetDealerInfoOfCobrandingGroup = (cobrandingGroupId, dealerId) => useEdgeDataFetcher(cobrandingGroupId && dealerId && `/cobranding/groups/${cobrandingGroupId}/dealers/${dealerId}?a=b`)

export const useEdgeGetRequestAppointmentSettings = dealerGuid => useEdgeDataFetcher(dealerGuid && `/appointmentrequestsettings/${dealerGuid}?a=b`)

export const useEdgeGetDealerReps = landingContext => {
  const [dealerReps] = useEdgeDataFetcher('/dealer-representatives/list?a=b', null, 'GET', 'dealerReps', true, undefined, true)
  landingContext.dealerReps = dealerReps
}

export const useLambdaDataFetcher = path => useDataFetcher(path && __PATH_PREFIX__ + path, true, undefined, undefined, undefined, 'lambda', true)

export const useLambdaDynamicDataFetcher = landingContext => {
  const interactionReady = process.env.NODE_ENV === 'production' && landingContext.globalState.interactionReady
  //latest true-client-ip and more, we can push more dynamic environment specific values when necessary
  const [lambdaDataFetched] = useLambdaDataFetcher(interactionReady && '/lambda.json')
  const lambdaData = process.env.NODE_ENV === 'production' ? lambdaDataFetched : {}
  landingContext.lambdaData = lambdaData
  const ip = lambdaData && (lambdaData['true-client-ip'] || '127.0.0.1')
  const qx = landingContext.qxData
  useEffect(() => {
    if (ip) {
      const item = QuestionUtils.getIpQuestion(ip)
      if (qx && !qx.find(question => question.questionId === item.questionId)) qx.push(item)
    }
  }, [ip, qx])
}

export const useEdgeHealthCheck = () => useEdgeDataFetcher('/health/ico?a=b', null, null, null, false, {}, null, true)

export const useEdgeDataFetcherHealthCheck = landingContext => {
  const [healthCheck] = useEdgeDataFetcher('/health/ico?a=b', null, null, null, false, {}, null, true)
  landingContext.health = healthCheck
}

export const useEdgeGetCommunicationReason = (offerId, emailId) => useEdgeDataFetcher(offerId && emailId && `/offers/${offerId}/adjuster-communication/${emailId}?a=b`, null, null, null, true, {}, null, false)
export const useEdgeRespondToCommunication = (offerId, emailId, response) => useEdgeDataFetcher(offerId && emailId && response && `/offers/${offerId}/adjuster-communication/${emailId}?a=b`, JSON.stringify(response), 'POST', null, false, {}, null, false)

export const useEdgeDataFetcherForVerification = (landingContext, pageContext, location) => {
  const { offer, vinResults } = landingContext
  const vin = (offer && offer.prospect.vehicle.vin) || (vinResults && vinResults.vin)

  //removed autocheck call (autoCheck now comes from vinDecode)
  //verificationIsFetching --> set this to always as autocheck now come from vindecode and already fetched at this point
  //verificationErrors array always either empty or 0 even when there is errors returned from the the VechicleInformation from edge
  const verificationErrors = []
  const verificationIsFetching = false
  let posVerificationData
  if (landingContext.autoCheckData) {
    posVerificationData = landingContext.autoCheckData
  }

  if (posVerificationData && posVerificationData.reportDate && posVerificationData.reportDate.includes('T')) {
    let reportDate = posVerificationData.reportDate.split('T')[0].split('-')
    let date = new Date(Date.UTC(reportDate[0], reportDate[1] - 1, parseInt(reportDate[2]) + 1))
    posVerificationData.reportDate = date.toLocaleDateString()
  }
  landingContext.autoCheckData = posVerificationData

  if (landingContext && landingContext.qxData) {
    const verifiedHistoryQuestionSource = landingContext.qxData.find(data => data.questionId === 'qx/AreQuestionsVerified')
    const verifiedMileage = landingContext.qxData.find(data => data.questionId === 'qx/VerifiedMileage')
    verifiedHistoryQuestionSource.value = verifiedHistoryQuestionSource.value ? verifiedHistoryQuestionSource.value : 0
    verifiedMileage.value = verifiedMileage.value ? verifiedMileage.value : 0
    const areQuestionsVerified = !verificationIsFetching && verificationErrors && !!verificationErrors.length && verificationErrors[0] === 200001

    if (!areQuestionsVerified && posVerificationData) {
      const { hasAccidents = null, isOriginalOwner = null, usedAsRentalCar = null, miles = null } = posVerificationData
      //Run when resubmitting an offer
      if (offer && ((offer.isExpired && offer.canBeResubmitted) || offer.quickQuote)) {
        landingContext.historyVerificationData = posVerificationData || undefined
        const offerIsOriginalOwner = offer.prospect.history.find(element => element.questionId === 'qp/83')
        const offerHasAccidents = offer.prospect.history.find(element => element.questionId === 'qp/124')
        const offerUsedAsRentalCar = offer.prospect.history.find(element => element.questionId === 'qp/125')
        const answerSourceInputArray = landingContext.qxData.find(data => data.questionId === 'qx/AnswerSourcesInput')
        const dealerModifiedQuestion = answerSourceInputArray.value.find(item => item.questionCode === 'DealerModifiedAccidentQuestion')

        offerIsOriginalOwner.value = isOriginalOwner
        if (offerHasAccidents.value === 'Yes' && hasAccidents === false && !offer.quickQuote) {
          dealerModifiedQuestion.value = true
        } else {
          offerHasAccidents.value = hasAccidents ? 'Yes' : 'No'
        }

        offerUsedAsRentalCar.value = usedAsRentalCar

        //store verified answers in qx to save values in db
        verifiedHistoryQuestionSource.value = 1
        verifiedMileage.value = miles
      } else if (offer == undefined || offer == null) {
        //Run during initial trip through funnel
        landingContext.historyVerificationData = posVerificationData || undefined
        verifiedHistoryQuestionSource.value = 1
        verifiedMileage.value = miles
      }
    } else {
      verifiedHistoryQuestionSource.value = 0
      verifiedMileage.value = 0
    }
  }
}

export const useForceAnswer = (landingContext, pageContext, location) => {
  const { offer, urlState } = landingContext

  // edited offer
  if (offer && offer.prospect && urlState) {
    if (!('qc/ConditionTierSelection' in urlState)) {
      urlState['qc/ConditionTierSelection'] = { v: offer.conditionTierSelection }
    }
    if (urlState['qp/65'] && Object.keys(urlState['qp/65']).length === 0) {
      urlState['qp/65'] = { v: 1 }
      // if quick quote
    } else if (offer.quickQuote && !('qp/65' in urlState)) {
      urlState['qp/65'] = { v: 1 }
    }
  } else if ((offer && urlState && offer === undefined) || (offer && urlState && offer === null)) {
    // consumer created offer
    if (!('qp/65' in urlState)) {
      urlState['qp/65'] = { v: 1 }
    }
  } else {
    // dealer created offer
    if (urlState && !('qp/65' in urlState)) {
      urlState['qp/65'] = { v: 1 }
    }
  }
}

export const useRunHistoryPage = landingContext => {
  const { experienceState, vinResults, historyVerificationData, urlState } = landingContext
  if (experienceState && !experienceState.isAdmin && vinResults && !vinResults.vehicleActivityApplicationDown && historyVerificationData && urlState) {
    urlState['qp/82'] = { v: 1 } //Still Making Payments
    urlState['qp/76'] = { v: 1 } //Insurance Paid for repair
    urlState['qp/17'] = { v: 0 } //Complete Maintenance records

    urlState['qp/81'] = { v: 1 }

    //hide if questions were answered by verified source
    const { hasAccidents = null, isOriginalOwner = null, usedAsRentalCar = null } = historyVerificationData
    urlState['qp/124'] = { v: hasAccidents ? 0 : 1 }
    urlState['qp/83'] = { v: isOriginalOwner ? 0 : 1 }
    urlState['qp/125'] = { v: usedAsRentalCar ? 0 : 1 }

    //When consumer resubmits if offer history accident is yes but autocheck says no resubmit offer w/ dealermodified offer
    const { offer, qxData } = landingContext
    if (offer && qxData && ((offer.isExpired && offer.canBeResubmitted) || offer.quickQuote)) {
      const answerSourceInputArray = qxData.find(data => data.questionId === 'qx/AnswerSourcesInput')
      const dealerModifiedQuestion = answerSourceInputArray.value.find(item => item.questionCode === 'DealerModifiedAccidentQuestion')
      const offerHasAccidents = offer.prospect.history.find(element => element.questionId === 'qp/124')

      if (offerHasAccidents.value === 'Yes' && hasAccidents === false && !offer.quickQuote) {
        dealerModifiedQuestion.value = true
        urlState['qp/124'] = { v: 0 }
      } else {
        urlState['qp/124'] = { v: hasAccidents ? 0 : 1 }
      }
    }
  }
}
